// InventoryMaster.jsx
import { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Spinner, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getPOTypes,
  GetMovementType,
} from "../Api/PurchaseOrder";
import { fetchPurchaseOrders } from "../Api/ViewPurchaseOrder";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";

const RECEIPT_TYPES = [
  { value: "Inventory", label: "Inventory" },
  { value: "Quality", label: "Quality" },
];

const todayISO = () => {
  const t = new Date();
  return t.toISOString().slice(0, 10); // YYYY-MM-DD
};

const InventoryMaster = () => {
  const [loading, setLoading] = useState(false);
  const [poTypes, setPoTypes] = useState([]);
  const [selectedPoType, setSelectedPoType] = useState("");
  const [movementTypes, setMovementTypes] = useState([]);
  const [selectedMovementType, setSelectedMovementType] = useState("");
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState("");
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const { user } = useAuth();

  // Vendors
  const [vendors, setVendors] = useState([]); // raw list
  const [vendorMap, setVendorMap] = useState({}); // code -> name map

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);

  // Load PO types once
  useEffect(() => {
    const loadPOTypes = async () => {
      setDropdownLoading(true);
      try {
        const data = await getPOTypes();
        setPoTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Failed to load PO Types");
      } finally {
        setDropdownLoading(false);
      }
    };
    loadPOTypes();
  }, []);

  // Load vendor master on mount
  useEffect(() => {
    let mounted = true;
    const loadVendors = async () => {
      try {
        const resp = await axios.get(`${Base_URL}/VendorMaster`);
        const list = Array.isArray(resp.data) ? resp.data : [];
        if (!mounted) return;
        setVendors(list);

        // Build a map — try common fields for code/name
        const map = {};
        list.forEach((v) => {
          // possible vendor code fields: vendorCode, code, merchantCode
          // possible name fields: vendorName, name, companyName
          const code = v.vendorCode ?? v.code ?? v.merchantCode ?? v.MerchantCode ?? null;
          const name = v.vendorName ?? v.name ?? v.companyName ?? v.VendorName ?? null;
          if (code) map[String(code)] = name ?? String(code);
        });
        if (mounted) setVendorMap(map);
      } catch (err) {
        console.error("Failed to load vendors", err);
        // non-fatal: continue without vendor name replacement
        toast.warning("Failed to load vendor master. Showing merchant codes.");
      }
    };
    loadVendors();
    return () => (mounted = false);
  }, []);

  // When PO Type changes -> fetch Movement Types
  useEffect(() => {
    const loadMovement = async () => {
      setMovementTypes([]);
      setSelectedMovementType("");
      setPurchaseOrders([]);
      setSelectedPurchaseOrder("");
      setPurchaseItems([]);

      if (!selectedPoType) return;

      setDropdownLoading(true);
      try {
        const data = await GetMovementType(selectedPoType);
        setMovementTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Failed to load Movement Types");
      } finally {
        setDropdownLoading(false);
      }
    };

    loadMovement();
  }, [selectedPoType]);

  // When movement type changes -> fetch Purchase Orders
  useEffect(() => {
    const loadPOs = async () => {
      setPurchaseOrders([]);
      setSelectedPurchaseOrder("");
      setPurchaseItems([]);

      if (!selectedMovementType) return;

      setDropdownLoading(true);
      try {
        const data = await fetchPurchaseOrders();
        setPurchaseOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Failed to load Purchase Orders");
      } finally {
        setDropdownLoading(false);
      }
    };

    loadPOs();
  }, [selectedMovementType]);

  // Fetch GST data for a specific item
  const fetchGstDataForItem = async (itemCode) => {
    if (!itemCode) {
      return { CGST: 0, IGST: 0, SGST: 0 };
    }

    try {
      console.log("Fetching GST data for item:", itemCode);
      
      const response = await axios.get(`${Base_URL}/VendorInvoice/GetGst?itemCode=${encodeURIComponent(itemCode)}`);
      console.log("GST API Response for item", itemCode, ":", response.data);

      if (response.data) {
        const data = response.data;
        
        // Handle different response structures
        let gstRates = { 
          CGST: 0, 
          IGST: 0, 
          SGST: 0 
        };

        // Extract GST rates from the response
        if (data.CGST_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.CGST_Percent) || 0,
            IGST: parseFloat(data.IGST_Percent) || 0,
            SGST: parseFloat(data.SGST_Percent) || 0
          };
        }
        // If response is an array, take the first item
        else if (Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          gstRates = {
            CGST: parseFloat(firstItem.CGST_Percent) || 0,
            IGST: parseFloat(firstItem.IGST_Percent) || 0,
            SGST: parseFloat(firstItem.SGST_Percent) || 0
          };
        }
        // Handle different property names
        else if (data.cgsT_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.cgsT_Percent) || 0,
            IGST: parseFloat(data.igsT_Percent) || 0,
            SGST: parseFloat(data.sgsT_Percent) || 0
          };
        }

        console.log("Final GST Rates for item", itemCode, ":", gstRates);
        return gstRates;
      }
      
      return { CGST: 0, IGST: 0, SGST: 0 };
      
    } catch (error) {
      console.error("Error fetching GST data for item", itemCode, ":", error);
      return { CGST: 0, IGST: 0, SGST: 0 };
    }
  };

  // Calculate GST amounts based on basic value and GST rates
  const calculateGstAmounts = (basicValue, cgstRate, igstRate, sgstRate) => {
    if (!basicValue) {
      return {
        cgstAmount: 0,
        igstAmount: 0,
        sgstAmount: 0,
        totalAmount: 0
      };
    }

    const amount = parseFloat(basicValue);
    const cgstAmount = (amount * (cgstRate || 0)) / 100;
    const igstAmount = (amount * (igstRate || 0)) / 100;
    const sgstAmount = (amount * (sgstRate || 0)) / 100;
    const totalAmount = amount + cgstAmount + igstAmount + sgstAmount;

    return {
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  // Recalculate GST when quantity received changes
  const recalculateGstForItem = (item) => {
    const basicValue = (item.quantityReceived || 0) * (item.price || 0);
    const gstAmounts = calculateGstAmounts(
      basicValue,
      item.cgstRate || 0,
      item.igstRate || 0,
      item.sgstRate || 0
    );

    return {
      ...item,
      basicValue,
      cgstAmount: gstAmounts.cgstAmount,
      igstAmount: gstAmounts.igstAmount,
      sgstAmount: gstAmounts.sgstAmount,
      totalAmount: gstAmounts.totalAmount
    };
  };

  // helper to fetch purchase items for a selected PO number
  const fetchPurchaseItems = async (poNumber) => {
    if (!poNumber) return;
    setItemsLoading(true);
    setPurchaseItems([]);
    try {
      const url = `${Base_URL}/PurchaseOrder/id?id=${encodeURIComponent(poNumber)}`;
      const resp = await axios.get(url);
      const items = Array.isArray(resp.data) ? resp.data : [];

      // Fetch GST data for each item and calculate amounts
      const mapped = await Promise.all(
        items.map(async (it) => {
          const origQty = parseFloat(it.Quantity ?? it.quantity ?? it.qty ?? 0) || 0;
          const price = parseFloat(it.Price ?? it.price ?? 0) || 0;
          const basicValue = origQty * price;

          // Fetch GST data for this item
          const itemCode = it.itemCode ?? it.Item_code ?? it.item_code ?? it.item ?? null;
          let gstRates = { CGST: 0, IGST: 0, SGST: 0 };
          
          if (itemCode) {
            gstRates = await fetchGstDataForItem(itemCode);
          }

          const gstAmounts = calculateGstAmounts(
            basicValue,
            gstRates.CGST,
            gstRates.IGST,
            gstRates.SGST
          );

          return {
            id: it.id ?? it.ID ?? it.Id ?? null,
            raw: it,
            item_code: itemCode,
            description: it.Description ?? it.description ?? it.desc ?? "",
            originalQuantity: origQty,
            unit: it.Unit ?? it.unit ?? it.uom ?? "",
            price: price,
            requestedDeliveryDate: it.Requested_Delivery_Date ?? it.requested_delivery_date ?? it.requestedDeliveryDate ?? null,

            // editable fields (defaults)
            quantityReceived: origQty,
            receiptDate: todayISO(),
            receiptType: RECEIPT_TYPES[0].value,
            remarks: "",
            basicValue: basicValue,
            
            // GST fields
            cgstRate: gstRates.CGST,
            igstRate: gstRates.IGST,
            sgstRate: gstRates.SGST,
            cgstAmount: gstAmounts.cgstAmount,
            igstAmount: gstAmounts.igstAmount,
            sgstAmount: gstAmounts.sgstAmount,
            totalAmount: gstAmounts.totalAmount
          };
        })
      );

      setPurchaseItems(mapped);
    } catch (err) {
      toast.error("Failed to load purchase items");
    } finally {
      setItemsLoading(false);
    }
  };

  // Calculate value for an item
  const calculateValue = (item) => {
    const qty = typeof item.quantityReceived === 'number' ? item.quantityReceived : parseFloat(item.quantityReceived) || 0;
    const priceValue = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
    return (qty * priceValue).toFixed(2);
  };

  // Handlers
  const handlePoTypeChange = (e) => setSelectedPoType(e.target.value);
  const handleMovementChange = (e) => setSelectedMovementType(e.target.value);

  const handlePurchaseOrderChange = (e) => {
    const selected = e.target.value;
    setSelectedPurchaseOrder(selected);
    fetchPurchaseItems(selected);
  };

  const handleRefreshItems = () => {
    if (selectedPurchaseOrder) fetchPurchaseItems(selectedPurchaseOrder);
  };

  // Editable field handlers for each line
  const handleQuantityReceivedChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      const num = value === "" ? "" : parseFloat(value);
      const orig = copy[index].originalQuantity ?? 0;

      if (num === "") {
        copy[index].quantityReceived = "";
      } else if (isNaN(num)) {
        return prev;
      } else {
        if (num > orig) {
          toast.warning(`Quantity received cannot exceed original quantity (${orig}).`);
          copy[index].quantityReceived = orig;
        } else if (num < 0) {
          copy[index].quantityReceived = 0;
        } else {
          copy[index].quantityReceived = num;
        }
      }

      // Recalculate GST
      copy[index] = recalculateGstForItem(copy[index]);

      return copy;
    });
  };

  const handleReceiptDateChange = (index, value) => {
    const max = todayISO();
    if (value > max) {
      toast.warning("Receipt date cannot be in the future. Setting to today.");
      value = max;
    }
    setPurchaseItems((prev) => {
      const copy = [...prev];
      copy[index].receiptDate = value;
      return copy;
    });
  };

  const handleReceiptTypeChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      copy[index].receiptType = value;
      return copy;
    });
  };

  const handleRemarksChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      copy[index].remarks = value;
      return copy;
    });
  };

  // Validate and open modal for confirmation
  const handleSaveReceipt = async () => {
    if (!selectedPurchaseOrder) {
      toast.error("Please select a Purchase Order before saving receipt.");
      return;
    }

    for (let i = 0; i < purchaseItems.length; i++) {
      const it = purchaseItems[i];
      if (it.quantityReceived === "" || it.quantityReceived == null) {
        toast.error(`Quantity Received required for line ${i + 1}.`);
        return;
      }
      if (typeof it.quantityReceived !== "number" || isNaN(it.quantityReceived)) {
        toast.error(`Quantity Received must be a number for line ${i + 1}.`);
        return;
      }
      if (it.quantityReceived > it.originalQuantity) {
        toast.error(`Quantity Received cannot exceed original for line ${i + 1}.`);
        return;
      }
    }

    // Open confirm modal
    setShowConfirmModal(true);
  };

  // Confirm Save: actually POST to backend (or console.log if not available)
  const confirmSaveReceipt = async () => {
    setModalSaving(true);

    const payload = {
      header: {
        poType: selectedPoType,
        movementType: selectedMovementType,
        purchaseOrderNumber: selectedPurchaseOrder,
        receiptCreatedDate: todayISO(),
        receivedBy: user?.userId ?? null,
      },
      items: purchaseItems.map((it) => ({
        item_code: it.item_code,
        description: it.description,
        originalQuantity: it.originalQuantity,
        quantityReceived: it.quantityReceived,
        unit: it.unit,
        price: it.price,
        requestedDeliveryDate: it.requestedDeliveryDate,
        receiptDate: it.receiptDate,
        receiptType: it.receiptType,
        remarks: it.remarks,
        basicValue: it.basicValue,
        cgstRate: it.cgstRate,
        igstRate: it.igstRate,
        sgstRate: it.sgstRate,
        cgstAmount: it.cgstAmount,
        igstAmount: it.igstAmount,
        sgstAmount: it.sgstAmount,
        totalAmount: it.totalAmount
      })),
    };

    try {
      // POST to the backend endpoint you implemented earlier
      const res = await axios.post(`${Base_URL}/Inventory`, payload);

      // If backend returned useful info (InventoryReceiptNumber, etc.) display it
      const data = res?.data;
      const irNumber = data?.InventoryReceiptNumber ?? data?.inventoryReceiptNumber ?? null;

      if (res.status >= 200 && res.status < 300) {
        toast.success(
          irNumber
            ? `Inventory receipt saved (${irNumber}).`
            : "Inventory receipt saved."
        );

        // Close modal and reset form/state
        setShowConfirmModal(false);
        setPurchaseItems([]);
        setSelectedPurchaseOrder("");
        setSelectedMovementType("");
        setSelectedPoType("");
      } else {
        // Non-2xx response handling (defensive)
        console.error("CreateInventory unexpected response:", res);
        toast.error("Failed to save inventory receipt.");
      }
    } catch (err) {
      const serverMessage =
        err?.response?.data ??
        err?.response?.data?.message ??
        err?.message ??
        "Unknown error";

      console.error("CreateInventory error:", err);
      toast.error(`Failed to save inventory receipt: ${serverMessage}`);

      // Also log the payload so you can replay if needed
      console.log("Inventory Receipt payload:", payload);
    } finally {
      setModalSaving(false);
    }
  };

  console.log(purchaseOrders);

  // --- NEW: helpers to get display names for selected codes ---
  const getPoTypeLabel = (code) => {
    if (!code) return "-";
    const found = poTypes.find((pt) =>
      (pt.prTypeCode ?? pt.code ?? pt.value ?? "") === code
    );
    if (found) {
      return found.prTypeDescription ?? found.description ?? found.label ?? code;
    }
    return code;
  };

  const getMovementTypeLabel = (code) => {
    if (!code) return "-";
    const found = movementTypes.find((mt) =>
      (mt.movementTypeCode ?? mt.code ?? mt.value ?? "") === code
    );
    if (found) {
      return found.movementTypeDescription ?? found.description ?? found.label ?? code;
    }
    return code;
  };

  // helper to resolve merchant code -> vendor name (fallback to code)
  const resolveVendorName = (merchantCode) => {
    if (!merchantCode) return "-";
    return vendorMap[String(merchantCode)] ?? merchantCode;
  };
  // --- end helpers ---

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h2 className="fields-title">Inventory Master — Inventory Receipt</h2>

        <Form className="fields-form">
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Inventory Type</Form.Label>
                <Form.Select value={selectedPoType} onChange={handlePoTypeChange} disabled={dropdownLoading}>
                  <option value="">Select PO Type</option>
                  {poTypes.map((pt) => (
                    <option
                      key={pt.prTypeCode ?? pt.code ?? pt.value ?? pt}
                      value={pt.prTypeCode ?? pt.code ?? pt.value ?? pt}
                    >
                      {pt.prTypeCode ? `${pt.prTypeCode} - ${pt.prTypeDescription ?? pt.description ?? pt.label}` : String(pt)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Movement Type</Form.Label>
                <Form.Select
                  value={selectedMovementType}
                  onChange={handleMovementChange}
                  disabled={!selectedPoType || dropdownLoading || movementTypes.length === 0}
                >
                  <option value="">Select Movement Type</option>
                  {movementTypes.map((mt) => (
                    <option
                      key={mt.movementTypeCode ?? mt.code ?? mt.value ?? mt}
                      value={mt.movementTypeCode ?? mt.code ?? mt.value ?? mt}
                    >
                      {mt.movementTypeCode ? `${mt.movementTypeCode} - ${mt.movementTypeDescription ?? mt.description ?? mt.label}` : String(mt)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Purchase Order</Form.Label>
                <Form.Select
                  value={selectedPurchaseOrder}
                  onChange={handlePurchaseOrderChange}
                  disabled={!selectedMovementType || dropdownLoading || purchaseOrders.length === 0}
                >
                  <option value="">Select Purchase Order</option>
                  {purchaseOrders.map((po) => {
                    const poNumber = po.purchaseOrderNumber ?? po.ID ?? po.id ?? "";
                    const merchantCode = po.merchantCode ?? po.MerchantCode ?? po.supplierCode ?? po.supplier ?? "";
                    const poDate = (po.pO_Date ?? po.poDate ?? po.createdOn)?.split ? String(po.pO_Date ?? po.poDate ?? po.createdOn).split("T")[0] : "";
                    const vendorName = resolveVendorName(merchantCode);
                    return (
                      <option
                        key={poNumber || JSON.stringify(po)}
                        value={poNumber}
                      >
                        {poNumber} - {vendorName}{poDate ? ` - ${poDate}` : ""}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12} className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedPoType("");
                  setSelectedMovementType("");
                  setSelectedPurchaseOrder("");
                  setPurchaseItems([]);
                }}
              >
                Reset
              </Button>
              <Button variant="outline-secondary" onClick={handleRefreshItems} disabled={!selectedPurchaseOrder}>
                Refresh Items
              </Button>
            </Col>
          </Row>
        </Form>

        <hr />

        <h4>Purchase Order Line Items</h4>

        {itemsLoading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {purchaseItems.length === 0 ? (
              <div>No purchase items to display.</div>
            ) : (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Code</th>
                      <th>Description</th>
                      <th>Original Qty</th>
                      <th>Qty Received</th>
                      <th>Unit</th>
                      <th>Price</th>
                      <th>Basic Value</th>
                      
                      {/* GST Headers */}
                      <th style={{ backgroundColor: '#dbeafe' }}>CGST %</th>
                      <th style={{ backgroundColor: '#dbeafe' }}>CGST Amount</th>
                      <th style={{ backgroundColor: '#dcfce7' }}>SGST %</th>
                      <th style={{ backgroundColor: '#dcfce7' }}>SGST Amount</th>
                      <th style={{ backgroundColor: '#fef9c3' }}>IGST %</th>
                      <th style={{ backgroundColor: '#fef9c3' }}>IGST Amount</th>
                      <th style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>Total Amount</th>
                      
                      <th>Requested Delivery</th>
                      <th>Receipt Date</th>
                      <th>Receipt Type</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseItems.map((it, idx) => (
                      <tr key={it.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td>{it.item_code}</td>
                        <td>{it.description}</td>
                        <td>{it.originalQuantity}</td>
                        <td style={{ minWidth: 140 }}>
                          <Form.Control
                            type="number"
                            step="0.0001"
                            min={0}
                            max={it.originalQuantity}
                            value={it.quantityReceived}
                            onChange={(e) => handleQuantityReceivedChange(idx, e.target.value)}
                          />
                        </td>
                        <td>{it.unit}</td>
                        <td>{it.price}</td>
                        <td style={{ fontWeight: '600' }}>
                          {it.basicValue?.toFixed(2)}
                        </td>
                        
                        {/* GST Fields */}
                        <td style={{ backgroundColor: '#f0f9ff' }}>{it.cgstRate?.toFixed(2)}%</td>
                        <td style={{ backgroundColor: '#f0f9ff' }}>{it.cgstAmount?.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#f0fdf4' }}>{it.sgstRate?.toFixed(2)}%</td>
                        <td style={{ backgroundColor: '#f0fdf4' }}>{it.sgstAmount?.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#fefce8' }}>{it.igstRate?.toFixed(2)}%</td>
                        <td style={{ backgroundColor: '#fefce8' }}>{it.igstAmount?.toFixed(2)}</td>
                        <td style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                          {it.totalAmount?.toFixed(2)}
                        </td>
                        
                        <td>{it.requestedDeliveryDate ? new Date(it.requestedDeliveryDate).toLocaleDateString() : ""}</td>
                        <td style={{ minWidth: 140 }}>
                          <Form.Control
                            type="date"
                            value={it.receiptDate}
                            max={todayISO()}
                            onChange={(e) => handleReceiptDateChange(idx, e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Select value={it.receiptType} onChange={(e) => handleReceiptTypeChange(idx, e.target.value)}>
                            {RECEIPT_TYPES.map((rt) => (
                              <option key={rt.value} value={rt.value}>
                                {rt.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={it.remarks}
                            onChange={(e) => handleRemarksChange(idx, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="7" className="text-end">Total Basic Value:</td>
                      <td>
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.basicValue) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      {/* GST Totals */}
                      <td colSpan="2" className="text-end" style={{ backgroundColor: '#dbeafe' }}>
                        Total CGST:
                      </td>
                      <td style={{ backgroundColor: '#dbeafe' }}>
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.cgstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="2" className="text-end" style={{ backgroundColor: '#dcfce7' }}>
                        Total SGST:
                      </td>
                      <td style={{ backgroundColor: '#dcfce7' }}>
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.sgstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="2" className="text-end" style={{ backgroundColor: '#fef9c3' }}>
                        Total IGST:
                      </td>
                      <td style={{ backgroundColor: '#fef9c3' }}>
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.igstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.totalAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </Table>

                <div className="d-flex gap-2 mt-3">
                  <Button variant="success" onClick={handleSaveReceipt}>
                    Save Receipt
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setPurchaseItems((prev) =>
                        prev.map((it) => ({
                          ...it,
                          quantityReceived: it.originalQuantity,
                          receiptDate: todayISO(),
                          receiptType: RECEIPT_TYPES[0].value,
                          remarks: "",
                        }))
                      );
                      toast.info("Receipt lines reset to defaults.");
                    }}
                  >
                    Reset Lines
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={() => !modalSaving && setShowConfirmModal(false)} size="xl" centered>
          <Modal.Header closeButton={!modalSaving}>
            <Modal.Title>Confirm Inventory Receipt</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <strong>PO Type:</strong> {getPoTypeLabel(selectedPoType)}
              </Col>
              <Col md={6}>
                <strong>Movement Type:</strong> {getMovementTypeLabel(selectedMovementType)}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Purchase Order:</strong> {selectedPurchaseOrder || "-"}
              </Col>
              <Col md={6}>
                <strong>Receipt Created Date:</strong> {todayISO()}
              </Col>
            </Row>

            <h6>Line Items</h6>
            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Code</th>
                  <th>Description</th>
                  <th>Original Qty</th>
                  <th>Qty Received</th>
                  <th>Basic Value</th>
                  <th>CGST %</th>
                  <th>CGST Amount</th>
                  <th>SGST %</th>
                  <th>SGST Amount</th>
                  <th>IGST %</th>
                  <th>IGST Amount</th>
                  <th>Total Amount</th>
                  <th>Receipt Date</th>
                  <th>Receipt Type</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {purchaseItems.map((it, idx) => (
                  <tr key={it.id ?? idx}>
                    <td>{idx + 1}</td>
                    <td>{it.item_code}</td>
                    <td>{it.description}</td>
                    <td>{it.originalQuantity}</td>
                    <td>{it.quantityReceived}</td>
                    <td>{it.basicValue}</td>
                    <td>{it.cgstRate}</td>
                    <td >{it.cgstAmount}</td>
                    <td >{it.sgstRate?.toFixed(2)}%</td>
                    <td >{it.sgstAmount?.toFixed(2)}</td>
                    <td >{it.igstRate?.toFixed(2)}%</td>
                    <td >{it.igstAmount?.toFixed(2)}</td>
                    <td>{it.totalAmount?.toFixed(2)}</td>
                    <td>{it.receiptDate}</td>
                    <td>{it.receiptType}</td>
                    <td>{it.remarks}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr >
                  <td colSpan="5" className="text-end">Totals:</td>
                  <td>
                    {purchaseItems.reduce((sum, it) => sum + (Number(it.basicValue) || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan="2" >
                    {purchaseItems.reduce((sum, it) => sum + (Number(it.cgstAmount) || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan="2" >
                    {purchaseItems.reduce((sum, it) => sum + (Number(it.sgstAmount) || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan="2">
                    {purchaseItems.reduce((sum, it) => sum + (Number(it.igstAmount) || 0), 0).toFixed(2)}
                  </td>
                  <td >
                    {purchaseItems.reduce((sum, it) => sum + (Number(it.totalAmount) || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={modalSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmSaveReceipt} disabled={modalSaving}>
              {modalSaving ? (
                <>
                  <Spinner animation="border" size="sm" /> Saving...
                </>
              ) : (
                "Confirm Save"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default InventoryMaster;